import cleanAssign from "@/clean-assign"
import {BackendFetch, Processes} from "@hantera/portal-app"
import {reactive, toRaw} from "vue"

export interface QueryFolder {
  id: string
  name: string
  children: QueryFolder[]
}

export interface Query {
  id?: string
  navigation: QueryNavigation
  columns: QueryColumn[]
  name: string
  folderId: string
}

export interface QueryColumn {
  queryColumnId: string
  queryPath: string[]
  fieldName: string
  label?: string
  size?: number
}

export interface QueryNavigation {
  edge?: string
  filter?: string
  navigation?: QueryNavigation[]
}

export const personalFolderId = '00000000-0000-0000-0000-000000000001';
export const sharedFolderId = '00000000-0000-0000-0000-000000000002';

export default function (portalFetch: BackendFetch, processes: Processes) {

  const folders = reactive<QueryFolder[]>([]);
  const queries = reactive<Record<string, Query>>({});

  async function refresh() {

    var response = await portalFetch("/queries", {
      method: 'GET'
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch queries: (${response.status}) ${await response.text()}`);
    }

    var data = await response.json();

    folders.splice(0, folders.length);
    folders.push(...data.folders);

    cleanAssign(queries, data.queries.reduce((res: Record<string, Query>, query: Query) => {
      res[query.id!] = query;
      return res;
    }, {} as Record<string, Query>));

    console.log("Queries refreshed", toRaw(folders), toRaw(queries));
  }

  refresh();

  setInterval(refresh, 1000 * 60 * 5);

  // TODO Synchronize across tabs using websockets with server

  async function saveQuery(query: Query) {

    const proc = processes.createProcess({
      description: `Saving query '${query.name}'`
    });

    try {

      let existingQuery: Query | undefined;
      if (query.id) {
        existingQuery = queries[query.id];

        if (!existingQuery) {
          proc.fail({
            message: "Tried to update a query that doesn't exist"
          });
          return;
        }
      }

      const response = await portalFetch("/queries/save-query", {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(query)
      });

      if (!response.ok) {
        throw new Error(await response.text());
      }

      const savedQuery = await response.json() as Query;

      if (existingQuery) {
        cleanAssign(existingQuery, savedQuery);
      } else {
        queries[savedQuery.id!] = savedQuery;
        existingQuery = savedQuery;
        console.log(queries);
      }

      proc.complete(`Query '${query.name}' saved`);
      return existingQuery;
    } catch (e) {
      if (e instanceof Error) {
        proc.fail(e);
      } else {
        proc.fail({ message: "An error occurred" });
      }
      return undefined;
    }
  }

  async function deleteQuery(query: Query) {
    const proc = processes.createProcess({
      description: `Deleting query '${query.name}'`
    });

    try {

      if (!query.id) {
        proc.fail({ message: "Query does not exist" });
        return;
      }

      const response = await portalFetch("/queries/delete-query", {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          queryId: query.id
        })
      });

      if (!response.ok) {
        throw new Error(await response.text());
      }

      delete queries[query.id];

      proc.complete(`Query '${query.name}' deleted`);
    }
    catch (e) {
      if (e instanceof Error) {
        proc.fail(e);
      } else {
        proc.fail({ message: "An error occurred" });
      }
    }
  }

  async function renameQuery(query: Query, newName: string) {
    const proc = processes.createProcess({
      description: `Renaming query '${query.name}'`
    });
    
    try {

      if (!query.id) {
        proc.fail({ message: "Query does not exist" });
        return;
      }
      
      const response = await portalFetch("/queries/rename-query", {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          queryId: query.id,
          newName
        })
      });

      if (!response.ok) {
        proc.fail({ message: await response.text() });
        return false;
      }

      query.name = newName;
      
      proc.complete(`Query '${query.name}' renamed`);

      return true;
    }
    catch (e) {
      proc.fail({ message: "An error occurred" });
      return false;
    }
  }

  async function createFolder(parentFolder: QueryFolder, name: string) {
    try {

      const response = await portalFetch("/queries/create-folder", {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          parentFolderId: parentFolder.id,
          name
        })
      });

      if (!response.ok) {
        throw new Error(await response.text());
      }

      const newFolder = await response.json() as QueryFolder;

      parentFolder.children.push(newFolder);

      return newFolder;
    }
    catch (e) {
      if (e instanceof Error) {
        processes.error(e);
      } else {
        processes.error({ message: "An error occurred" });
      }
    }
  }

  async function renameFolder(folderId: string, newName: string) {
    try {

      const folder = findFolderRecursive(folders);
      if (folder === undefined) {
        processes.error({ message: "Folder not found" });
        return false
      }

      const response = await portalFetch("/queries/rename-folder", {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          folderId,
          newName
        })
      });

      if (!response.ok) {
        throw new Error(await response.text());
      }

      folder.name = newName;
      
      return true;
    }
    catch (e) {
      if (e instanceof Error) {
        processes.error(e);
      } else {
        processes.error({ message: "An error occurred" });
      }
      return false;
    }

    function findFolderRecursive(folders: QueryFolder[]): QueryFolder | undefined {
      for (const folder of folders) {
        if (folder.id === folderId) {
          return folder;
        }

        const found = findFolderRecursive(folder.children);
        if (found) {
          return found;
        }
      }

      return undefined;
    }
  }

  async function deleteFolder(folderId: string) {
    try {

      const response = await portalFetch("/queries/delete-folder", {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          folderId
        })
      });

      if (!response.ok) {
        throw new Error(await response.text());
      }

      removeFolderRecursive(folders);
      function removeFolderRecursive(folders: QueryFolder[]) {
        for (let i = 0; i < folders.length; i++) {
          if (folders[i].id === folderId) {
            folders.splice(i, 1);
            return;
          }

          removeFolderRecursive(folders[i].children);
        }
      }
      
      return true;
    }
    catch (e) {
      if (e instanceof Error) {
        processes.error(e);
      } else {
        processes.error({ message: "An error occurred" });
      }
      return false;
    }
  }

  async function moveQuery(queryId: string, folderId: string) {
    try {

      const query = queries[queryId];
      if (!query) {
        throw new Error("Query not found");
      }

      const response = await portalFetch("/queries/move-query", {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          queryId,
          folderId
        })
      });

      if (!response.ok) {
        throw new Error(await response.text());
      }

      query.folderId = folderId;
      
      return true;
    }
    catch (e) {
      if (e instanceof Error) {
        processes.error(e);
      } else {
        processes.error({ message: "An error occurred" });
      }
      return false;
    }
  }

  async function moveFolder(folderId: string, parentFolderId: string) {
    try {

      const response = await portalFetch("/queries/move-folder", {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          folderId,
          parentFolderId
        })
      });

      if (!response.ok) {
        throw new Error(await response.text());
      }

      var data = await response.json();

      folders.splice(0, folders.length);
      folders.push(...data.folders);
    
      return true;
    }
    catch (e) {
      if (e instanceof Error) {
        processes.error(e);
      } else {
        processes.error({ message: "An error occurred" });
      }
      return false;
    }
  }

  return {
    folders: folders as QueryFolder[],
    queries: queries as Record<string, Query>,
    saveQuery,
    deleteQuery,
    renameQuery,
    createFolder,
    renameFolder,
    deleteFolder,
    moveQuery,
    moveFolder,
  };

}